import { RecordFormDialog, formatRecordDate } from "@taunggyi-plus/design-system";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import { Card, ManagementListToolbar, PaginatedList } from "@taunggyi-plus/design-system";
import { EventParticipants } from "./events";
type Row = { id: string; [key: string]: unknown };
type Mode = "customer" | "provider" | "admin";
const text = (r: Row, k: string) => String(r[k] ?? "");
const money = (v: unknown) => `${(Number(v) / 100).toLocaleString()} MMK`;
const services: Record<string, string> = {
  hotel: "Link Stay",
  merchant: "Link Food",
  taxi: "Link Ride",
};
const unit = (service: string) =>
  service === "hotel"
    ? "per room / night"
    : service === "merchant"
      ? "per item"
      : "per fixed-route ride";
export function FestivalWorkspace({ event, mode }: { event: string; mode: Mode }) {
  const { client, user } = useAuth();
  const [record, setRecord] = useState<Row | null>(null),
    [offers, setOffers] = useState<Row[]>([]),
    [orgs, setOrgs] = useState<Row[]>([]),
    [fees, setFees] = useState<Row[]>([]),
    [applications, setApplications] = useState<Row[]>([]);
  const [org, setOrg] = useState(""),
    [search, setSearch] = useState(""),
    [service, setService] = useState("all"),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true),
    [version, setVersion] = useState(0);
  const base =
    mode === "customer" ? "/events" : mode === "admin" ? "/more/events" : "/more/provider-events";
  const load = useCallback(async () => {
    if (!client || !user) return;
    const result = await client.from("platform_events").select("*").eq("id", event).maybeSingle();
    if (result.error) throw Error(result.error.message);
    if (!result.data) throw Error("Event is not available.");
    setRecord(result.data);
    if (mode === "customer") {
      const r = await client.rpc("published_event_services", { event });
      if (r.error) throw Error(r.error.message);
      setOffers(r.data ?? []);
    }
    if (mode === "provider") {
      const owned = await client.rpc("my_event_organizations");
      if (owned.error) throw Error(owned.error.message);
      const organizations = ((owned.data ?? []) as Row[]).filter((organization) =>
        ["hotel", "merchant", "taxi_group"].includes(text(organization, "kind")),
      );
      const ids = organizations.map((value) => value.id);
      const [o, f, p] = await Promise.all([
        Promise.resolve({ data: organizations, error: null }),
        client.from("event_service_fees").select("*").eq("event_id", event),
        ids.length
          ? client
              .from("event_participations")
              .select("*")
              .eq("event_id", event)
              .in("organization_id", ids)
          : Promise.resolve({ data: [], error: null }),
      ]);
      const failure = [o, f, p].find((r) => r.error)?.error;
      if (failure) throw Error(failure.message);
      setOrgs(o.data ?? []);
      setFees(f.data ?? []);
      setApplications(p.data ?? []);
    }
  }, [client, user, event, mode]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLoading(true);
      void load()
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const selected = orgs.find((o) => o.id === org) ?? orgs[0];
  const application = applications.find((p) => p.organization_id === selected?.id);
  const fee = fees.find((f) => f.service === selected?.kind);
  const filtered = offers.filter(
    (p) =>
      (service === "all" || p.provider_kind === service) &&
      `${p.title} ${p.resource_name} ${p.description}`.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <section className="filtered-list-stack">
      <a className="more-back-link" href={base}>
        ← Events
      </a>
      {loading && <p role="status">Loading event…</p>}
      {error && <p role="alert">{error}</p>}
      {record && (
        <>
          <h2>{text(record, "title")}</h2>
          <p>{text(record, "description")}</p>
          <p>
            {text(record, "venue")} · {formatRecordDate(text(record, "starts_on"))} –{" "}
            {formatRecordDate(text(record, "ends_on"))}
          </p>
          {mode === "customer" && (
            <>
              <ManagementListToolbar
                query={search}
                onQueryChange={setSearch}
                searchLabel="Search festival services or partners"
                resultCount={filtered.length}
                filters={[
                  {
                    label: "Service",
                    value: service,
                    onChange: setService,
                    options: [
                      { value: "all", label: "All festival services" },
                      ...Object.entries(services).map(([value, label]) => ({ value, label })),
                    ],
                  },
                ]}
                onClear={() => {
                  setSearch("");
                  setService("all");
                }}
              />
              <div className="festival-offer-list">
                <PaginatedList
                  layout="cards"
                  items={filtered}
                  resetKey={`${search}:${service}`}
                  label="Festival services"
                  renderItem={(p) => <FestivalBooking key={p.id} offer={p} />}
                />
              </div>
              {!loading && !filtered.length && (
                <Card>No approved services are available for this event yet.</Card>
              )}
            </>
          )}
          {mode === "provider" && (
            <>
              <label className="field">
                Your business
                <select value={selected?.id ?? ""} onChange={(e) => setOrg(e.target.value)}>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {text(o, "name")}
                    </option>
                  ))}
                </select>
              </label>
              <Card>
                <p>
                  One festival fee includes all approved festival listings. Regular prices remain
                  separate.
                </p>
                {application ? (
                  <p>
                    Participation: {text(application, "status")} · {money(application.fee_minor)}.
                    See Finance for payment and receipts.
                  </p>
                ) : fee ? (
                  <p>Participation fee: {money(fee.fee_minor)}</p>
                ) : (
                  <p>This service is not accepting applications.</p>
                )}
                {!application && fee && record.status === "published" && (
                  <button
                    className="button"
                    disabled={busy}
                    onClick={async () => {
                      if (!client || !selected || busy) return;
                      setBusy(true);
                      setError("");
                      try {
                        const r = await client.rpc("apply_to_event", { event, org: selected.id });
                        if (r.error) throw Error(r.error.message);
                        await load();
                      } catch (e) {
                        setError(e instanceof Error ? e.message : "Could not join event");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Apply to join · {money(fee.fee_minor)}
                  </button>
                )}
                <a href="/finance">Fees and receipts</a>
              </Card>
              {selected &&
                application &&
                ["pending", "approved"].includes(text(application, "status")) && (
                  <FestivalPartnerListings key={selected.id} event={record} org={selected} />
                )}
            </>
          )}
          {mode === "admin" && (
            <>
              <p>
                Manage partners and their festival prices for this event only. One participation fee
                covers every approved listing.
              </p>
              <EventParticipants key={version} event={event} mode="admin" />
              <FestivalAdminListings event={event} onChange={() => setVersion((v) => v + 1)} />
            </>
          )}
        </>
      )}
    </section>
  );
}
function FestivalBooking({ offer }: { offer: Row }) {
  const { client } = useAuth();
  const [open, setOpen] = useState(false),
    [values, setValues] = useState<Record<string, string>>({
      quantity: "1",
      rooms: "1",
      guests: "1",
    }),
    [message, setMessage] = useState(""),
    [order, setOrder] = useState(""),
    [busy, setBusy] = useState(false);
  const request = useRef(crypto.randomUUID()),
    lock = useRef(false);
  const kind = text(offer, "provider_kind");
  const fields = [
    { key: "name", label: "Your name", type: "text" },
    { key: "phone", label: "Phone", type: "tel" },
    ...(kind === "merchant"
      ? [
          { key: "quantity", label: "Quantity", type: "number" },
          { key: "address", label: "Delivery address", type: "text" },
        ]
      : kind === "hotel"
        ? [
            { key: "check_in", label: "Check-in", type: "date" },
            { key: "check_out", label: "Check-out", type: "date" },
            { key: "rooms", label: "Rooms", type: "number" },
            { key: "guests", label: "Guests", type: "number" },
          ]
        : [{ key: "pickup_at", label: "Pickup date and time", type: "datetime-local" }]),
  ];
  const multiplier =
    kind === "merchant"
      ? Number(values.quantity)
      : kind === "hotel"
        ? Number(values.rooms) *
          Math.max(
            0,
            (Date.parse(values.check_out ?? "") - Date.parse(values.check_in ?? "")) / 86400000,
          )
        : 1;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!client || lock.current) return;
    lock.current = true;
    setBusy(true);
    setMessage("");
    try {
      const details = {
        ...values,
        expected_price_minor: Number(offer.price_minor),
        ...(kind === "taxi" ? { pickup_at: new Date(values.pickup_at!).toISOString() } : {}),
      };
      const r = await client.rpc("place_event_order", {
        listing: offer.id,
        details,
        request_id: request.current,
      });
      if (r.error) throw Error(r.error.message);
      setOrder(r.data.id);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not submit. Retry with the same details.");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <Card className="festival-offer-card">
      <span className="status-pill">{services[kind]}</span>
      <h3>{text(offer, "title")}</h3>
      <p>{text(offer, "resource_name")}</p>
      <strong>
        {money(offer.price_minor)} · {unit(kind)}
      </strong>
      {kind === "taxi" && (
        <p>
          {text(offer, "pickup")} → {text(offer, "dropoff")}
        </p>
      )}
      <p>{text(offer, "description")}</p>
      <p>{text(offer, "terms")}</p>
      <p>
        {formatRecordDate(text(offer, "starts_on"))} – {formatRecordDate(text(offer, "ends_on"))}
      </p>
      {!order && (
        <button className="button" onClick={() => setOpen(!open)}>
          {open ? "Close booking" : "Book festival offer"}
        </button>
      )}
      {open && !order && (
        <RecordFormDialog
          title="Book festival offer"
          open
          hideTrigger
          onClose={() => setOpen(false)}
        >
          <form className="form-grid" onSubmit={submit}>
            {fields.map((f) => (
              <label className="field" key={f.key}>
                {f.label}
                <input
                  type={f.type}
                  required
                  min={f.type === "number" ? 1 : undefined}
                  value={values[f.key] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  disabled={busy}
                />
              </label>
            ))}
            {Number.isFinite(multiplier) && multiplier > 0 && (
              <strong>Total: {money(Number(offer.price_minor) * multiplier)}</strong>
            )}
            <p>This booking uses the festival price shown above.</p>
            <button className="button" disabled={busy}>
              {busy ? "Submitting…" : "Confirm festival booking"}
            </button>
          </form>
        </RecordFormDialog>
      )}
      {message && <p role="alert">{message}</p>}
      {order && (
        <p role="status">
          Festival booking submitted. <a href="/orders">View orders</a>
        </p>
      )}
    </Card>
  );
}
function FestivalPartnerListings({ event, org }: { event: Row; org: Row }) {
  const { client } = useAuth();
  const [resources, setResources] = useState<Row[]>([]),
    [items, setItems] = useState<Row[]>([]),
    [offers, setOffers] = useState<Row[]>([]),
    [resource, setResource] = useState(""),
    [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const kind = org.kind === "taxi_group" ? "taxi" : text(org, "kind");
  const table =
    kind === "hotel" ? "hotel_properties" : kind === "merchant" ? "merchant_shops" : "taxi_groups";
  const load = useCallback(async () => {
    if (!client) return;
    const [r, p] = await Promise.all([
      client.from(table).select("id,display_name").eq("organization_id", org.id),
      client
        .from("special_promotion_requests")
        .select("*")
        .eq("event_id", event.id)
        .eq("organization_id", org.id),
    ]);
    if (r.error || p.error) throw Error((r.error ?? p.error)!.message);
    setResources(r.data ?? []);
    setOffers(p.data ?? []);
    const ids = (r.data ?? []).map((v: { id: string }) => v.id);
    if (kind !== "taxi" && ids.length) {
      const i = await client
        .from(kind === "hotel" ? "hotel_room_types" : "merchant_items")
        .select("*")
        .in(kind === "hotel" ? "property_id" : "shop_id", ids);
      if (i.error) throw Error(i.error.message);
      setItems(i.data ?? []);
    }
  }, [client, table, org.id, event.id, kind]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load().catch((e) => setError(e.message)), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const selected = resource || resources[0]?.id || "";
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!client || busy) return;
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError("");
    try {
      const r = await client.rpc("request_event_listing", {
        event: event.id,
        provider: kind,
        resource: selected,
        item: kind === "taxi" ? null : f.get("item"),
        title: f.get("title"),
        description: f.get("description"),
        terms: f.get("terms"),
        starts: f.get("starts"),
        ends: f.get("ends"),
        price: Math.round(Number(f.get("price")) * 100),
        pickup: f.get("pickup") ?? "",
        dropoff: f.get("dropoff") ?? "",
      });
      if (r.error) throw Error(r.error.message);
      setOpen(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not request festival listing");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="filtered-list-stack">
      <h3>Your festival services</h3>
      <p>
        Create a separate festival offer from your room, food item or taxi route. Its title, terms
        and price apply only inside this festival.
      </p>
      <button className="button" onClick={() => setOpen(!open)}>
        {open ? "Close form" : "Add festival service"}
      </button>
      {error && <p role="alert">{error}</p>}
      {open && (
        <Card>
          <RecordFormDialog
            title="Add festival service"
            open
            hideTrigger
            onClose={() => setOpen(false)}
          >
            <form className="form-grid" onSubmit={submit}>
              <label className="field">
                Business location
                <select value={selected} onChange={(e) => setResource(e.target.value)} required>
                  {resources.map((r) => (
                    <option key={r.id} value={r.id}>
                      {text(r, "display_name")}
                    </option>
                  ))}
                </select>
              </label>
              {kind !== "taxi" && (
                <label className="field">
                  {kind === "hotel" ? "Room type" : "Food item"}
                  <select name="item" required>
                    {items
                      .filter(
                        (i) =>
                          (kind === "hotel" ? i.property_id : i.shop_id) === selected &&
                          (kind === "hotel" ? i.is_active : i.is_available),
                      )
                      .map((i) => (
                        <option key={i.id} value={i.id}>
                          {text(i, "name")}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              {[
                { key: "title", label: "Festival service title", type: "text" },
                { key: "description", label: "Description", type: "text" },
                { key: "terms", label: "Offer terms", type: "text" },
                { key: "price", label: `Festival price (MMK, ${unit(kind)})`, type: "number" },
                ...(kind === "taxi"
                  ? [
                      { key: "pickup", label: "Fixed route: from", type: "text" },
                      { key: "dropoff", label: "Fixed route: to", type: "text" },
                    ]
                  : []),
                { key: "starts", label: "Offer start", type: "date" },
                { key: "ends", label: "Offer end", type: "date" },
              ].map((f) => (
                <label className="field" key={f.key}>
                  {f.label}
                  <input
                    name={f.key}
                    type={f.type}
                    required
                    min={
                      f.type === "number"
                        ? 0.01
                        : f.type === "date"
                          ? text(event, "starts_on")
                          : undefined
                    }
                    max={f.type === "date" ? text(event, "ends_on") : undefined}
                    step={f.type === "number" ? 0.01 : undefined}
                    defaultValue={
                      f.key === "starts"
                        ? text(event, "starts_on")
                        : f.key === "ends"
                          ? text(event, "ends_on")
                          : undefined
                    }
                  />
                </label>
              ))}
              <p>
                Included in your participation fee. Admin must approve this listing before customers
                can see it.
              </p>
              <button className="button" disabled={busy}>
                Submit festival listing
              </button>
            </form>
          </RecordFormDialog>
        </Card>
      )}
      {offers.map((p) => (
        <Card key={p.id}>
          <h4>{text(p, "title")}</h4>
          <p>
            {text(p, "status")} · {money(p.event_price_minor)} {unit(kind)}
          </p>
          <p>{text(p, "admin_note")}</p>
        </Card>
      ))}
    </section>
  );
}
function FestivalAdminListings({ event, onChange }: { event: string; onChange: () => void }) {
  const { client } = useAuth();
  const [offers, setOffers] = useState<Row[]>([]),
    [search, setSearch] = useState(""),
    [status, setStatus] = useState("all"),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!client) return;
    const r = await client
      .from("special_promotion_requests")
      .select("*")
      .eq("event_id", event)
      .order("created_at", { ascending: false });
    if (r.error) throw Error(r.error.message);
    setOffers(r.data ?? []);
  }, [client, event]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load().catch((e) => setError(e.message)), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  async function act(p: Row, decision: string, note = "") {
    if (!client || busy) return;
    setBusy(true);
    setError("");
    try {
      const r =
        decision === "hide"
          ? await client.rpc("hide_event_listing", { event, listing: p.id })
          : await client.rpc("review_event_listing", {
              event,
              listing: p.id,
              decision,
              priority: 50,
              note,
            });
      if (r.error) throw Error(r.error.message);
      await load();
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update listing");
    } finally {
      setBusy(false);
    }
  }
  const filtered = offers.filter(
    (p) =>
      (status === "all" || p.status === status) &&
      `${p.title} ${p.resource_name}`.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <section className="filtered-list-stack">
      <h3>Services for this event</h3>
      <ManagementListToolbar
        query={search}
        onQueryChange={setSearch}
        searchLabel="Search event partner or service"
        resultCount={filtered.length}
        filters={[
          {
            label: "Status",
            value: status,
            onChange: setStatus,
            options: ["all", "pending", "approved", "rejected", "cancelled"].map((value) => ({
              value,
              label: value,
            })),
          },
        ]}
        onClear={() => {
          setSearch("");
          setStatus("all");
        }}
      />
      {error && <p role="alert">{error}</p>}
      <PaginatedList
        items={filtered}
        resetKey={`${search}:${status}`}
        label="Event listing reviews"
        renderItem={(p) => (
          <Card key={p.id}>
            <h4>{text(p, "title")}</h4>
            <p>
              {text(p, "resource_name")} · {services[text(p, "provider_kind")]}
            </p>
            <strong>
              {money(p.event_price_minor)} · {unit(text(p, "provider_kind"))}
            </strong>
            <p>{text(p, "description")}</p>
            <p>{text(p, "terms")}</p>
            {p.provider_kind === "taxi" && (
              <p>
                {text(p, "event_pickup")} → {text(p, "event_dropoff")}
              </p>
            )}
            <p>
              {text(p, "starts_on")} – {text(p, "ends_on")} · {text(p, "status")}
            </p>
            {p.status === "pending" && (
              <form
                className="form-grid"
                onSubmit={(e) => {
                  e.preventDefault();
                  void act(p, "approved", String(new FormData(e.currentTarget).get("note") ?? ""));
                }}
              >
                <label className="field">
                  Review note
                  <input name="note" maxLength={500} />
                </label>
                <button className="button" disabled={busy}>
                  Approve festival listing
                </button>
                <button
                  type="button"
                  className="button button--danger"
                  disabled={busy}
                  onClick={(e) =>
                    void act(
                      p,
                      "rejected",
                      String(new FormData(e.currentTarget.form!).get("note") ?? ""),
                    )
                  }
                >
                  Reject
                </button>
              </form>
            )}
            {p.status === "approved" && (
              <button
                className="button button--danger"
                disabled={busy}
                onClick={() => void act(p, "hide")}
              >
                Hide from this event
              </button>
            )}
          </Card>
        )}
      />
    </section>
  );
}
